import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { DateField } from '@components/ui/DateField/DateField';
import { useSiteTags } from '../hooks/useSiteTags';
import { useSiteEquipment } from '../hooks/useSiteEquipment';
import { useDailyRouteDateRules } from './hooks/useDailyRouteDateRules';
import type { AnalyticsTag, AnalyticsEquipment } from '@apis/analytics';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import type { IEntityKind } from './IncidentReportCard';
import { CardIcon } from '../CardIcon';

/**
 * What the card emits via `onSubmit`. The page handler converts the `Date`
 * to a `YYYY-MM-DD` string when building the create-report payload.
 */
export interface IDailyRouteSubmission {
  entityKind: IEntityKind;
  entityId: string;
  entityName: string;
  entityNick: string;
  date: Date;
}

interface IDailyRouteCardProps {
  isSelected: boolean;
  onSelect: (name: 'dailyRoute') => void;
  onSubmit: (payload: IDailyRouteSubmission) => void;
}

/** `${tag.name}-${tag.tagNick}` — falls back to nickname when name is empty. */
function tagToOption(tag: AnalyticsTag): ISelectionOption {
  const label = tag.name ? `${tag.name}-${tag.tagNick}` : tag.tagNick;
  return { id: tag.tagId, label };
}

/** `${equipment.typeNick}-${equipment.name}` — falls back to typeNick when name is empty. */
function equipmentToOption(equipment: AnalyticsEquipment): ISelectionOption {
  const label = equipment.name
    ? `${equipment.typeNick}-${equipment.name}`
    : equipment.typeNick;
  return { id: equipment.equipmentId, label };
}

export function DailyRouteCard({ isSelected, onSelect, onSubmit }: IDailyRouteCardProps) {
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const [entityKind, setEntityKind] = useState<IEntityKind>('tag');
  const [entityId, setEntityId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(null);

  // Hooks must run unconditionally — both queries are gated server-side via
  // `enabled: !!siteId`, so an empty siteId simply parks them in idle.
  const tagsQuery = useSiteTags(siteId);
  const equipmentQuery = useSiteEquipment(siteId);

  const tagOptions: ISelectionOption[] = useMemo(
    () => (tagsQuery.data ?? []).map(tagToOption),
    [tagsQuery.data],
  );
  const equipmentOptions: ISelectionOption[] = useMemo(
    () => (equipmentQuery.data ?? []).map(equipmentToOption),
    [equipmentQuery.data],
  );

  const options = entityKind === 'tag' ? tagOptions : equipmentOptions;
  const isLoading = entityKind === 'tag' ? tagsQuery.isLoading : equipmentQuery.isLoading;
  const dateRules = useDailyRouteDateRules({ siteId, entityKind, entityId });
  const canSubmit = entityId !== null && date !== null;

  return (
    <ReportCard
      name="dailyRoute"
      title={t('analytics.cards.dailyRoute.title')}
      description={t('analytics.cards.dailyRoute.description')}
      icon={<CardIcon name="Route" />}
      isSelected={isSelected}
      onSelect={() => onSelect('dailyRoute')}
      onSubmit={() => {
        if (!entityId || !date) return;
        // Pluck name + nick from the cached query data so the recent-reports
        // rail can render entity context without re-fetching at display time.
        const tag = entityKind === 'tag' ? tagsQuery.data?.find((x) => x.tagId === entityId) : undefined;
        const eq = entityKind === 'equipment' ? equipmentQuery.data?.find((x) => x.equipmentId === entityId) : undefined;
        // `||` (not `??`): the BFF returns `''` when an upstream record has
        // no name field, and we want that to fall through to the next option
        // rather than render an empty heading.
        const entityName = tag?.name || eq?.name || entityId;
        const entityNick = tag?.tagNick || eq?.typeNick || entityId;
        onSubmit({ entityKind, entityId, entityName, entityNick, date });
      }}
      submitLabel={t('analytics.createButton')}
      submitDisabled={!canSubmit}
    >
      <SegmentedControl
        value={entityKind}
        onChange={(v) => {
          setEntityKind(v);
          setEntityId(null);
        }}
        options={[
          { id: 'tag', label: t('analytics.filters.switchToTag') },
          { id: 'equipment', label: t('analytics.filters.switchToEquipment') },
        ]}
        ariaLabel={t('analytics.filters.tag')}
      />
      <SingleSelect
        value={entityId}
        onChange={setEntityId}
        options={options}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={entityKind === 'tag' ? t('analytics.filters.tag') : t('analytics.filters.equipment')}
        disabled={isLoading || options.length === 0}
      />
      <DateField
        value={date}
        onChange={setDate}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={t('analytics.filters.dateSingle')}
        {...dateRules}
      />
    </ReportCard>
  );
}
